import MoneyInput from '../components/MoneyInput';
import FileUpload from '../components/FileUpload';
import React, { useEffect, useState } from 'react';
import { FaPaperclip } from 'react-icons/fa';
import CompanyContactModal, { emptyOrganizationContact } from '../components/CompanyContactModal';
import './SchedaMedica.css';

export const medicalEntryTypes = {
  prontoSoccorso: 'Pronto soccorso',
  visiteMediche: 'Visita medica',
  esamiStrumentali: 'Esame strumentale',
  cartelleCliniche: 'Cartella clinica',
  terapie: 'Terapia o fisioterapia',
  medicinaliAltro: 'Medicinale o altra spesa'
};

export const newMedicalEntry = () => ({
  isNew: true,
  originalSection: null,
  section: 'visiteMediche',
  id: Date.now(),
  values: { Data: '', Descrizione: '', Struttura: '', Note: '', 'Pagate da': '', Importo: '', GiorniPrognosi: '', FinePrognosi: '', PrestazioneGratuita: 'false' }
});

const prognosisSections = ['prontoSoccorso', 'visiteMediche'];
const prognosisEndDate = (date, days) => {
  const count = Number.parseInt(days, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '') || !Number.isSafeInteger(count) || count < 1) return '';
  const result = new Date(`${date}T12:00:00`);
  result.setDate(result.getDate() + count);
  const year = result.getFullYear();
  const month = String(result.getMonth() + 1).padStart(2, '0');
  const day = String(result.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function MedicalEntryModal({ editor, onChange, onClose, onSave, schedaId, onDocumentUploaded }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [centerSearch, setCenterSearch] = useState(editor?.values?.Struttura || '');
  const [centerResults, setCenterResults] = useState([]);
  const [centerForm, setCenterForm] = useState(null);
  const [centerError, setCenterError] = useState('');

  useEffect(() => {
    if (editor?.section !== 'terapie' || centerForm) return undefined;
    if (editor?.values?.CentroId && centerSearch.trim() === String(editor.values.Struttura || '').trim()) {
      setCenterResults([]);
      return undefined;
    }
    const query = centerSearch.trim();
    if (query.length < 2) { setCenterResults([]); return undefined; }
    const timer = setTimeout(() => fetch(`/api/contatti?tipo=${encodeURIComponent('Centro fisioterapico')}&ricerca=${encodeURIComponent(query)}`)
      .then(response => response.ok ? response.json() : [])
      .then(rows => setCenterResults(rows.slice(0, 5))).catch(() => setCenterResults([])), 220);
    return () => clearTimeout(timer);
  }, [centerSearch, centerForm, editor?.section, editor?.values?.CentroId, editor?.values?.Struttura]);

  if (!editor) return null;

  const update = patch => onChange(previous => ({ ...previous, ...patch }));
  const updateValues = patch => onChange(previous => ({ ...previous, values: { ...previous.values, ...patch } }));
  const hasPrognosis = prognosisSections.includes(editor.section);
  const isFree = hasPrognosis && editor.values.PrestazioneGratuita === 'true';
  const calculatedFinePrognosi = hasPrognosis ? prognosisEndDate(editor.values.Data, editor.values.GiorniPrognosi) : '';
  const selectCenter = center => {
    setCenterSearch(center.nome);
    setCenterResults([]);
    updateValues({ Struttura: center.nome, CentroId: String(center.id), CentroConvenzionato: center.convenzionato ? 'true' : 'false' });
  };
  const saveNewCenter = async center => {
    setCenterError('');
    try {
      const response = await fetch('/api/contatti', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...center, tipo: 'Centro fisioterapico', tipo_soggetto: 'azienda' }) });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Centro non salvato');
      selectCenter({ ...center, id: result.id });
      setCenterForm(null);
    } catch (cause) { setCenterError(cause.message); }
  };
  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      let nextEditor = { ...editor, values: { ...editor.values, FinePrognosi: calculatedFinePrognosi } };
      if (file) {
        if (!schedaId) throw new Error('Salva prima la pratica');
        if (file.size > 15 * 1024 * 1024) throw new Error('Il file supera 15 MB');
        const response = await fetch(`/api/documenti/schede/${schedaId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/octet-stream',
            'X-File-Name': encodeURIComponent(file.name),
            'X-File-Type': file.type,
            'X-Category': 'Medico',
            'X-Description': encodeURIComponent(`Documento clinico: ${medicalEntryTypes[editor.section]}`)
          },
          body: file
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || 'Caricamento non riuscito');
        nextEditor = { ...nextEditor, values: { ...nextEditor.values, DocumentoId: String(result.id), DocumentoNome: file.name } };
        onDocumentUploaded?.(result);
      }
      await onSave(nextEditor);
    } catch (cause) {
      setError(cause.message || 'Salvataggio non riuscito');
    } finally {
      setBusy(false);
    }
  };

  return <div className="practice-modal-overlay" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <form className="practice-modal medical-editor" role="dialog" aria-modal="true" aria-labelledby="medical-editor-title" onSubmit={submit}>
      <div className="practice-modal-header"><div><span className="medical-eyebrow">DIARIO CLINICO</span><h2 id="medical-editor-title">{editor.isNew ? 'Nuova voce' : 'Modifica voce'}</h2></div><button type="button" className="practice-modal-close" aria-label="Chiudi finestra" onClick={onClose}>×</button></div>
      <div className="medical-editor-grid">
        <label>Tipo<select value={editor.section} onChange={event => update({ section: event.target.value })}>{Object.entries(medicalEntryTypes).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label>Data<input type="date" value={editor.values.Data || ''} onChange={event => updateValues({ Data: event.target.value })} /></label>
        <label className="medical-editor-wide">Descrizione<input autoFocus required type="text" placeholder="Es. Visita ortopedica" value={editor.values.Descrizione || ''} onChange={event => updateValues({ Descrizione: event.target.value })} /></label>
        {editor.section === 'terapie' ? <label className="medical-center-picker">Centro fisioterapico
          <input type="text" autoComplete="off" placeholder="Scrivi il nome del centro" value={centerSearch}
            onChange={event => { setCenterSearch(event.target.value); updateValues({ Struttura: event.target.value, CentroId: '', CentroConvenzionato: '' }); }} />
          {!editor.values.CentroId && centerSearch.trim().length >= 2 && <span className="medical-center-suggestions">{centerResults.map(center => <button type="button" key={center.id} onClick={() => selectCenter(center)}>
            <strong>{center.nome}</strong>{center.convenzionato && <small>Convenzionato CISA</small>}</button>)}
            {!centerResults.some(center => center.nome.toLocaleLowerCase('it-IT') === centerSearch.trim().toLocaleLowerCase('it-IT')) &&
              <button type="button" className="create" onClick={() => setCenterForm(emptyOrganizationContact(centerSearch.trim(), 'Centro fisioterapico'))}>+ Aggiungi nuovo centro fisioterapico</button>}</span>}
          {editor.values.CentroId && <small className={editor.values.CentroConvenzionato === 'true' ? 'medical-center-linked agreed' : 'medical-center-linked'}>Collegato alla Rubrica{editor.values.CentroConvenzionato === 'true' ? ' · Convenzionato CISA' : ''}</small>}
        </label> : <label>Struttura o professionista<input type="text" value={editor.values.Struttura || ''} onChange={event => updateValues({ Struttura: event.target.value })} /></label>}
        {hasPrognosis && <><label>Giorni di prognosi<input type="number" inputMode="numeric" min="1" placeholder="Es. 10" value={editor.values.GiorniPrognosi || ''} onChange={event => updateValues({ GiorniPrognosi: event.target.value.replace(/\D/g, '') })} /></label><label>Fine prognosi<input type="date" value={calculatedFinePrognosi} readOnly /></label></>}
        {hasPrognosis && <label className="medical-editor-wide medical-free-row"><span>Prestazione SSN / gratuita</span><span className="medical-ios-toggle"><input type="checkbox" checked={isFree} onChange={event => updateValues({ PrestazioneGratuita: event.target.checked ? 'true' : 'false', 'Pagate da': event.target.checked ? '' : editor.values['Pagate da'], Importo: event.target.checked ? '0' : editor.values.Importo })} /><i aria-hidden="true" /><b>{isFree ? 'Sì' : 'No'}</b></span></label>}
        <label>Pagato da<select required={!isFree} disabled={isFree} value={isFree ? '' : (editor.values['Pagate da'] || '')} onChange={event => updateValues({ 'Pagate da': event.target.value })}><option value="" disabled>{isFree ? 'Nessun pagamento' : 'Seleziona'}</option><option value="Cliente">Cliente</option><option value="Studio CISA">Studio CISA</option><option value="In sospeso">In sospeso</option></select></label>
        <label>Importo<MoneyInput type="text" inputMode="decimal" placeholder="0,00" disabled={isFree} value={isFree ? '0,00' : (editor.values.Importo || '')} onChange={event => updateValues({ Importo: event.target.value })} /></label>
        <label className="medical-editor-wide">Prognosi, referto o note<textarea rows="3" value={editor.values.Note || ''} onChange={event => updateValues({ Note: event.target.value })} /></label>
        <label className="medical-editor-wide medical-entry-file"><span>Documento collegato</span><FileUpload accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={event => setFile(event.target.files?.[0] || null)} /><small>{file?.name || editor.values.DocumentoNome || 'Puoi allegare subito il documento alla voce.'}</small></label>
      </div>
      {error && <p className="practice-home-error">{error}</p>}
      <p className="medical-editor-hint"><FaPaperclip /> La voce e il documento saranno disponibili anche nella Scheda medica.</p>
      <div className="practice-modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Annulla</button><button type="submit" className="primary-button" disabled={busy}>{busy ? 'Salvataggio…' : editor.isNew ? 'Crea voce' : 'Salva modifiche'}</button></div>
    </form>
    {centerForm && <CompanyContactModal value={centerForm} onChange={setCenterForm} onClose={() => setCenterForm(null)}
      onSubmit={saveNewCenter} error={centerError} title="Nuovo centro fisioterapico" submitLabel="Salva e collega"
      contactType="Centro fisioterapico" showAgreement />}
  </div>;
}
